/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, Input} from '@angular/core';
import * as _ from 'lodash';
import {
  isDashOutputConfig, isHlsOutputConfig, isMpeg4OutputConfig, isQuicktimeOutputConfig
} from "./transcode-config";
import type {TranscodeConfig} from "./transcode-config";
import {APP_SHARED} from '../app-shared';
import {GuardTypePipe} from '../filters/guard-type.pipe';

@Component({
  standalone: true,
  imports: [...APP_SHARED, GuardTypePipe],
  selector: 'transcode-config',
  template: `
    <div class="tab-content" *ngIf="displayConfig; else noTranscodeConfig">
      <p>Transcoding is <span class="label" [ngClass]="{'label-success': displayConfig.enabled, 'label-warning': !displayConfig.enabled}">{{displayConfig.enabled ? 'Enabled' : 'Disabled'}}</span></p>
      <h4>Transcode Settings</h4>
      <div *ngFor="let output of outputEntries">
        <div class="row">
          <label class="col-md-4 control-label">Output Name</label>
          <div class="form-control-static">{{output.key}}</div>
        </div>
        <div class="row">
          <label class="col-md-4 control-label">Output Type</label>
          <div class="form-control-static">{{output.value.type | uppercase}}</div>
        </div>
        <div class="row">
          <label class="col-md-4 control-label">Audio normalisation</label>
          <div class="form-control-static">{{output.value.audioNormalisation ? 'Enabled' : 'Disabled'}}</div>
        </div>
        <div *ngIf="output.value | guardType: isDashOutputConfig as dashOutputConfig">
          <div class="row" *ngFor="let streamConfig of dashOutputConfig.dashStreamConfigs; let i = index">
            <label class="col-md-4 control-label">Stream {{i}} max bitrate</label>
            <div class="form-control-static">{{streamConfig.maxBitrate}} bits/sec</div>
          </div>
        </div>
        <div *ngIf="output.value | guardType: isHlsOutputConfig as hlsOutputConfig">
          <div class="row" *ngFor="let streamConfig of hlsOutputConfig.hlsStreamConfigs; let i = index">
            <label class="col-md-4 control-label">Stream {{i}} max bitrate</label>
            <div class="form-control-static">{{streamConfig.maxBitrate}} bits/sec</div>
          </div>
        </div>
        <div *ngIf="output.value | guardType: isMpeg4OutputConfig as mpeg4OutputConfig">
          <div class="row">
            <label class="col-md-4 control-label">Max bitrate</label>
            <div class="form-control-static">{{mpeg4OutputConfig.maxBitrate}} bits/sec</div>
          </div>
          <div class="row">
            <label class="col-md-4 control-label">Height</label>
            <div class="form-control-static">{{mpeg4OutputConfig.height}} pixels</div>
          </div>
        </div>
        <div *ngIf="output.value | guardType: isQuicktimeOutputConfig as quicktimeOutputConfig">
          <div class="row">
            <label class="col-md-4 control-label">Max bitrate</label>
            <div class="form-control-static">{{quicktimeOutputConfig.maxBitrate}} bits/sec</div>
          </div>
          <div class="row">
            <label class="col-md-4 control-label">Height</label>
            <div class="form-control-static">{{quicktimeOutputConfig.height}} pixels</div>
          </div>
        </div>
        <hr>
      </div>
    </div>
    <ng-template #noTranscodeConfig>
      <p class="form-control-static">No transcode configuration available.</p>
    </ng-template>
  `
})
export class TranscodeConfigComponent {
  displayConfig: TranscodeConfig | null = null;
  outputEntries: Array<{key: string; value: any}> = [];

  @Input()
  set transcodeConfig(config: TranscodeConfig | string | null | undefined) {
    this.displayConfig = this.normalizeConfig(config);
    this.outputEntries = this.buildOutputEntries(this.displayConfig);
  }

  // type guards used in template
  isDashOutputConfig = isDashOutputConfig;
  isHlsOutputConfig = isHlsOutputConfig;
  isMpeg4OutputConfig = isMpeg4OutputConfig;
  isQuicktimeOutputConfig = isQuicktimeOutputConfig;

  private normalizeConfig(config: TranscodeConfig | string | null | undefined): TranscodeConfig | null {
    if (!config) {
      return null;
    }

    let normalizedConfig: any = config;
    if (_.isString(normalizedConfig)) {
      try {
        normalizedConfig = JSON.parse(normalizedConfig);
      } catch {
        return null;
      }
    }

    if (!_.isObject(normalizedConfig)) {
      return null;
    }

    const normalizedConfigObj: any = normalizedConfig;
    let outputs: any = {};
    if (normalizedConfigObj.outputs instanceof Map) {
      normalizedConfigObj.outputs.forEach((value: any, key: string) => {
        outputs[key] = value;
      });
    } else if (_.isObject(normalizedConfigObj.outputs)) {
      outputs = normalizedConfigObj.outputs;
    }

    return {
      enabled: !!normalizedConfigObj.enabled,
      outputs
    };
  }

  private buildOutputEntries(config: TranscodeConfig | null): Array<{key: string; value: any}> {
    if (!config?.outputs) {
      return [];
    }

    return _.map(config.outputs as any, (value, key) => {
      return {
        key,
        value: this.normalizeOutput(value)
      };
    });
  }

  private normalizeOutput(output: any): any {
    if (!_.isObject(output)) {
      return output;
    }

    const outputObj: any = output;

    return {
      ...outputObj,
      type: _.toLower(outputObj.type),
      dashStreamConfigs: _.isArray(outputObj.dashStreamConfigs) ? outputObj.dashStreamConfigs : [],
      hlsStreamConfigs: _.isArray(outputObj.hlsStreamConfigs) ? outputObj.hlsStreamConfigs : []
    };
  }
}
